import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Company } from '@lciapps/lci-core';
import { Observable } from 'rxjs';
import { ClubDetails } from './shared/models/club-details.model';
import { Detail } from './shared/models/detail';
import { PaginationResponse } from './shared/models/pagination-response';

@Injectable()
export class MetricsService {
  constructor(private http: HttpClient) { }

  /**
   * Returns activity causes
   * @returns {any}
   */
  public getActivityCauses(): any {
    return this.http.get(`mobileapi://activities/activitycauses`) as Observable<
      any
    >;
  }

  /**
   * Returns constitional area summary by ca Id
   * @param {number} constitutionalAreaId
   * @param {number} startDate
   * @param {number} endDate
   * @param {number} pageNumber
   * @param {number} rowsPerPage
   * @returns {Observable<Detail>}
   */
  public getConstitutionalAreaSummary(
    constitutionalAreaId: number,
    startDate: number,
    endDate: number,
    pageNumber: number,
    rowsPerPage: number,
    activityTypeId: number
  ): Observable<Detail> {
    const params: any = {
      startDate,
      endDate,
      pageNumber,
      rowsPerPage,
      activityTypeId,
    };
    return this.http.get(
      `reportingApi://constitutionalArea/${constitutionalAreaId}/activitysummary`,
      { params }
    ) as Observable<Detail>;
  }

  /**
   * Returns Activity Summary
   * @param {number} startDate
   * @param {number} endDate
   * @param {number} pageNumber
   * @param {number} rowsPerPage
   * @returns {Observable<Detail>}
   */
  public getLCIActivitySummary(
    startDate: number,
    endDate: number,
    pageNumber: number,
    rowsPerPage: number,
    activityTypeId: number
  ): Observable<Detail> {
    const params: any = {
      startDate,
      endDate,
      pageNumber,
      rowsPerPage,
      activityTypeId,
    };
    return this.http.get(`reportingApi://lci/activitysummary`, {
      params,
    }) as Observable<Detail>;
  }

  /**
   * Returns club activity summary by club id
   * @param {number} clubId
   * @param {number} startDate
   * @param {number} endDate
   * @param {number} pageNumber
   * @param {number} rowsPerPage
   * @returns {Observable<Detail>}
   */
  public getClubActivitySummary(
    clubId: number,
    startDate: number,
    endDate: number,
    pageNumber: number,
    rowsPerPage: number,
    activityTypeId: number
  ): Observable<Detail> {
    const params: any = {
      startDate,
      endDate,
      pageNumber,
      rowsPerPage,
      activityTypeId,
    };
    return this.http.get(`reportingApi://clubs/${clubId}/activitysummary`, {
      params,
    }) as Observable<Detail>;
  }

  /**
   * Returns district activity summary by district id
   * @param {number} districtId
   * @param {number} startDate
   * @param {number} endDate
   * @param {number} pageNumber
   * @param {number} rowsPerPage
   * @returns {Observable<Detail>}
   */
  public getDistrictActivitySummary(
    districtId: number,
    startDate: number,
    endDate: number,
    pageNumber: number,
    rowsPerPage: number,
    activityTypeId: number,
    companyType?: string
  ): Observable<Detail> {
    const params: any = {
      startDate,
      endDate,
      pageNumber,
      rowsPerPage,
      activityTypeId,
      companyType
    };
    return this.http.get(
      `reportingApi://districts/${districtId}/activitysummary`,
      { params }
    ) as Observable<Detail>;
  }

  /**
   * Returns multiple district activity summary from multiple district id
   * @param {number} multipleDistrictId
   * @param {number} startDate
   * @param {number} endDate
   * @param {number} pageNumber
   * @param {number} rowsPerPage
   * @returns {Observable<Detail>}
   */
  public getMultipleDistrictActivitySummary(
    multipleDistrictId: number,
    startDate: number,
    endDate: number,
    pageNumber: number,
    rowsPerPage: number,
    activityTypeId: number,
    companyType?: string
  ): Observable<Detail> {
    const params: any = {
      startDate,
      endDate,
      pageNumber,
      rowsPerPage,
      activityTypeId,
      companyType
    };
    return this.http.get(
      `reportingApi://multipledistricts/${multipleDistrictId}/activitysummary`,
      { params }
    ) as Observable<Detail>;
  }

  /**
   * Returns constitional area activity
   * @param {number} constitutionalAreaId
   * @param {number} startDate
   * @param {number} endDate
   * @param {number} pageNumber
   * @param {number} rowsPerPage
   * @returns {Observable<Array<Detail>>}
   */
  public getConstitutionalAreaActivity(
    constitutionalAreaId: number,
    startDate: number,
    endDate: number,
    pageNumber: number,
    rowsPerPage: number,
    activityTypeId: number
  ): Observable<Detail[]> {
    const params: any = {
      startDate,
      endDate,
      pageNumber,
      rowsPerPage,
      activityTypeId,
    };
    return this.http.get(
      `reportingApi://constitutionalArea/${constitutionalAreaId}/activitydetail`,
      { params }
    ) as Observable<Detail[]>;
  }

  /**
   * Returns LCI Activity
   * @param {number} startDate
   * @param {number} endDate
   * @param {number} pageNumber
   * @param {number} rowsPerPage
   * @returns {Observable<Array<Detail>>}
   */
  public getLCIActivity(
    startDate: number,
    endDate: number,
    pageNumber: number,
    rowsPerPage: number,
    activityTypeId: number
  ): Observable<Detail[]> {
    const params: any = {
      startDate,
      endDate,
      pageNumber,
      rowsPerPage,
      activityTypeId,
    };
    return this.http.get(`reportingApi://lci/activitydetail`, {
      params,
    }) as Observable<Detail[]>;
  }

  /**
   *
   * @param {number} clubId
   * @param {number} startDate
   * @param {number} endDate
   * @param {number} pageNumber
   * @param {number} rowsPerPage
   * @returns {Observable<Array<Detail>>}
   */
  public getClubActivity(
    clubId: number,
    startDate: number,
    endDate: number,
    pageNumber: number,
    rowsPerPage: number,
    activityTypeId: number
  ): Observable<Detail[]> {
    const params: any = {
      startDate,
      endDate,
      pageNumber,
      rowsPerPage,
      activityTypeId,
    };
    return this.http.get(`reportingApi://clubs/${clubId}/activitydetail`, {
      params,
    }) as Observable<Detail[]>;
  }

  /**
   * Return district activity from district id
   * @param {number} districtId
   * @param {number} startDate
   * @param {number} endDate
   * @param {number} pageNumber
   * @param {number} rowsPerPage
   * @returns {Observable<Array<Detail>>}
   */
  public getDistrictActivity(
    districtId: number,
    startDate: number,
    endDate: number,
    pageNumber: number,
    rowsPerPage: number,
    activityTypeId: number
  ): Observable<Detail[]> {
    const params: any = {
      startDate,
      endDate,
      pageNumber,
      rowsPerPage,
      activityTypeId,
    };
    return this.http.get(
      `reportingApi://districts/${districtId}/activitydetail`,
      { params }
    ) as Observable<Detail[]>;
  }

  /**
   * Return multiple district id
   * @param {number} multipleDistrictID
   * @param {number} startDate
   * @param {number} endDate
   * @param {number} pageNumber
   * @param {number} rowsPerPage
   * @returns {Observable<Array<Detail>>}
   */
  public getMultipleDistrictActivity(
    multipleDistrictID: number,
    startDate: number,
    endDate: number,
    pageNumber: number,
    rowsPerPage: number,
    activityTypeId: number
  ): Observable<Detail[]> {
    const params: any = {
      startDate,
      endDate,
      pageNumber,
      rowsPerPage,
      activityTypeId,
    };
    return this.http.get(
      `reportingApi://multipledistricts/${multipleDistrictID}/activitydetail`,
      { params }
    ) as Observable<Detail[]>;
  }

  /**
   * Return activity impact
   * @param {string} userId
   * @param {string} startDate
   * @param {string} endDate
   * @param {number} pageNumber
   * @param {number} rowsPerPage
   * @returns {Observable<Array<Detail>>}
   */
  public getUsersActivityImpact(
    userId: string,
    startDate: string,
    endDate: string,
    pageNumber: number,
    rowsPerPage: number
  ): Observable<any> {
    const params: any = {
      userId,
      startDate,
      endDate,
      pageNumber,
      rowsPerPage,
    };
    return this.http.get(`mobileapi://users/${userId}/activityimpact`, {
      params,
    }) as Observable<any>;
  }

  /**
   * Return activity impact summary for user with userId
   * @param {string} userId
   * @param {string} startDate
   * @param {string} endDate
   */
  public getActivityImpactSummary(
    userId: string,
    startDate: string,
    endDate: string
  ): Observable<any> {
    const params: any = {
      startDate,
      endDate,
    };
    return this.http.get(`mobileapi://users/${userId}/activityimpactsummary`, {
      params,
    }) as Observable<any>;
  }

  /**
   * Search companies
   * @param {string} searchText
   * @param {string} filter
   * @param {string} orderBy
   * @param {number} pageNumber
   * @param {number} pageSize
   * @returns {Observable<PaginationResponse<Company>>}
   */
  public searchCompanies(
    searchText?: string,
    filter?: string,
    orderBy?: string,
    pageNumber?: number,
    pageSize?: number
  ): Observable<PaginationResponse<Company>> {
    const params: any = {};
    if (searchText) {
      params.searchText = searchText;
    }
    if (filter) {
      params.filter = filter;
    }
    if (orderBy) {
      params.orderBy = orderBy;
    }
    if (pageNumber !== undefined) {
      params.pageNumber = pageNumber.toString();
    }
    if (pageSize !== undefined) {
      params.pageSize = pageSize.toString();
    }
    return this.http.get('mobileapi://companies/search', {
      params,
    }) as Observable<PaginationResponse<Company>>;
  }



  /**
   * Returns club activity summary by club id
   * @param {number} clubId
   * @param {number} startDate
   * @param {number} endDate
   * @returns {Observable<ClubDetails>}
   */
  public getClubDetails(
    clubId: number,
    startDate: number,
    endDate: number
  ): Observable<ClubDetails> {
    const params: any = {
      startDate,
      endDate,
    };
    return this.http.get(`reportingApi://clubs/${clubId}/clubdetail`, {
      params,
    }) as Observable<ClubDetails>;
  }
}
